/**
 * The offset of an in-progress multi-row drag: published by the row the user grabbed and mirrored
 * by the rest of the block, so the selection moves as one.
 *
 * It travels as CSS custom properties on the list container rather than as React state — the offset
 * changes every animation frame, and routing it through state would re-render every row in the room.
 * dnd-kit only ever hands a transform to the grabbed row, so the others have no other way to follow.
 */
const OFFSET_X_VAR = "--multi-drag-x";
const OFFSET_Y_VAR = "--multi-drag-y";

/**
 * Transform for a row mirroring the grabbed row, shifted by `rowShift` row heights so a scattered
 * selection closes up into a solid block while it's in flight. The shift is expressed as a
 * percentage of the row's own height, which is why no measuring is needed anywhere.
 */
export const getMultiDragTransform = (rowShift: number) =>
  `translate3d(var(${OFFSET_X_VAR}, 0px), calc(var(${OFFSET_Y_VAR}, 0px) + (${rowShift} * 100%)), 0)`;

/** Publishes the offset to every row beneath `element`. Written straight to the DOM, no re-render. */
export const setMultiDragOffset = (element: HTMLElement | null, x: number, y: number) => {
  element?.style.setProperty(OFFSET_X_VAR, `${x}px`);
  element?.style.setProperty(OFFSET_Y_VAR, `${y}px`);
};

/**
 * How far each dragged row must shift, in row heights, to stack up contiguously — anchored on the
 * grabbed row so the row under the cursor never jumps.
 */
export const getCollapseShifts = (draggedUuids: Array<string>, orderedUuids: Array<string>, grabbedUuid: string): Map<string, number> => {
  const grabbedIndex = orderedUuids.indexOf(grabbedUuid);
  const grabbedPosition = draggedUuids.indexOf(grabbedUuid);
  if (grabbedIndex === -1 || grabbedPosition === -1) return new Map();

  return new Map(draggedUuids.map((uuid, position) => [uuid, grabbedIndex + position - grabbedPosition - orderedUuids.indexOf(uuid)]));
};
